import { useState, useRef, useCallback } from 'react';
import { api } from './useApi';

export interface VoiceAgentState {
  status: 'idle' | 'thinking' | 'responding' | 'done' | 'error';
  quickResponse: string;
  fullResponse: string;
  error: string;
}

const initialState: VoiceAgentState = {
  status: 'idle',
  quickResponse: '',
  fullResponse: '',
  error: '',
};

export function useVoiceAgent() {
  const [state, setState] = useState<VoiceAgentState>(initialState);
  const wsRef = useRef<WebSocket | null>(null);

  const send = useCallback(async (transcript: string) => {
    // Close any existing connection
    wsRef.current?.close();

    const me = await api.getMe().catch(() => null);
    const userId = me?.id ?? null;

    // Native app connects directly to prod; web uses vite proxy (/ws → ws://localhost:3001)
    const isNative = window.location.protocol === 'capacitor:' || (window.location.hostname === 'localhost' && window.location.port === '');
    const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = isNative
      ? 'wss://airscribe.app/ws/voice-agent'
      : `${wsProto}//${window.location.host}/ws/voice-agent`;

    setState({ status: 'thinking', quickResponse: '', fullResponse: '', error: '' });

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'text', text: transcript, userId }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === 'thinking') {
          setState(s => ({ ...s, status: 'thinking' }));
        } else if (msg.type === 'claude_quick') {
          setState(s => ({ ...s, quickResponse: msg.text }));
        } else if (msg.type === 'claude_chunk') {
          setState(s => ({ ...s, status: 'responding', fullResponse: s.fullResponse + msg.text }));
        } else if (msg.type === 'claude_done') {
          setState(s => ({ ...s, status: 'done', fullResponse: msg.text || s.fullResponse }));
          ws.close();
        } else if (msg.type === 'error') {
          setState(s => ({ ...s, status: 'error', error: msg.message }));
          ws.close();
        }
      } catch (e) {
        console.error('[useVoiceAgent] parse error:', e);
      }
    };

    ws.onerror = () => {
      setState(s => ({ ...s, status: 'error', error: 'Connection error' }));
    };

    ws.onclose = () => {
      wsRef.current = null;
    };
  }, []);

  const dismiss = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    setState(initialState);
  }, []);

  return { state, send, dismiss };
}
